import {
  Box,
  FormHelperText,
  Typography,
  FormControlLabel,
  Checkbox as MuiCheckbox,
  useMediaQuery,
} from "@mui/material";
import { Button, ButtonVariantOption } from "@/components/Button";
import { PagePaper, SectionPaper } from "@/components/Paper";
import { FeedGrid } from "@/features/acquireMultipleBooks/components/FeedGrid";
import React, { useEffect, useState } from "react";
import {
  type BookTypeEng,
  type AcquireMultipleBookHandoffData,
} from "@/types/acquirebook";
import { type BookRequestCheckboxStatuses } from "@/features/acquireMultipleBooks/types";
import { AlertBar } from "@/features/acquireMultipleBooks/components/AlertBar";
import { useFeatureFlags } from "@/configs/featureFlag";
import {
  DISTINCT_MESSAGE,
  makeOptionsInitialState,
  distinct,
} from "@/features/acquireMultipleBooks/routes/AcquireMultipleBooks";
import Grid from "@mui/material/Grid";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { UserConfigGetAPI } from "@/features/accountSettings/api/UserConfigAPI";
import { CTextFieldIdentifyName } from "@/components/TextField/CTextFieldIdentifyName";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { CTextFieldPastDays } from "@/components/TextField/CTextFieldPastDays";
import { Label } from "@/components/Label";
import { ConfirmAcquireMultipleBooksDialog } from "@/features/acquireMultipleBooks/components/ConfirmAcquireMultipleBooksDialog";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import {
  AllMattersAnalyzeOptionsCheckbox,
  type AllMattersAnalyzeOptions,
} from "@/components/AcquireBook/AllMattersAnalyzeOptionsCheckbox";
import {
  PAST_DAYS_MIN,
  PAST_DAYS_MAX,
  PAST_DAYS_MIN_MESSAGE,
  PAST_DAYS_MAX_MESSAGE,
  PAST_DAYS_TYPE_MESSAGE,
} from "@/utils/pastDaysValidation";

interface IAcquireMultipleBooksModalProps {
  handleSubmit: (
    rows: AcquireMultipleBookHandoffData[],
    checkBoxOptions: BookRequestCheckboxStatuses[],
    pastDays: number | "",
    withOwnerAnalyze: boolean,
    withAllMattersAnalyze: boolean,
    reuseOnlyOwn: boolean,
    identificationName: string,
    cancelLoading: () => void,
    setDisableConfirmModalCancelButton?: React.Dispatch<
      React.SetStateAction<boolean>
    >,
    allMattersAnalyzeOptions?: AllMattersAnalyzeOptions
  ) => void;
  rows: AcquireMultipleBookHandoffData[];
  // NOTE: 2025/01/27 hotfixにて地番または家屋番号を非表示に変更
  isOpenedTitleSearch?: boolean;
}

/**
 * 不動産種別ラベルからBookTypeを取得する
 * @param label
 * @returns BookTypeEng
 */
export const getBookTypeFromRealEstateTypeLabel = (
  label: string
): BookTypeEng => {
  if (label === "土地") {
    return "LAND";
  } else if (label === "区分建物") {
    return "CONDOMINIUM";
  } else if (label === "建物") {
    return "BUILDING";
  } else {
    return "ONE_BUILDING";
  }
};

/** 中央寄せのFlexBox用スタイル（解析オプション周りのBoxで再利用） */
const centerFlexBoxSx = {
  mb: 1,
  display: "flex" as const,
  justifyContent: "center" as const,
};

export const AcquireMultipleBooksModal: React.FC<
  IAcquireMultipleBooksModalProps
> = ({ handleSubmit, rows, isOpenedTitleSearch = false }) => {
  // ユーザー設定取得
  const userConfig = UserConfigGetAPI();

  // 確認画面のモーダルを表示するかしないかを決めるフラグ
  const [openModalFlag, setOpenModalFlag] = React.useState(false);

  const [withOwnerAnalyze, setWithOwnerAnalyze] = React.useState(false);

  const [withAllMattersAnalyze, setWithAllMattersAnalyze] =
    React.useState(false);

  const [allMattersAnalyzeOptions, setAllMattersAnalyzeOptions] =
    React.useState<AllMattersAnalyzeOptions>({
      includeTitleSection: true,
      includeKoku: true,
      includeOtsuku: true,
      analyzeMethodType: "rule_based",
    });

  // 解析オプション未選択時のエラーメッセージ（送信ブロック時のみ表示）
  const [allMattersOptionsError, setAllMattersOptionsError] = React.useState<
    string | null
  >(null);

  const [reuseOnlyOwn, setReuseOnlyOwn] = React.useState(false);

  const isSmallHeightScreen = useMediaQuery("(max-height:900px)");
  const isReadOnly = useReadOnlyUser();

  // 重複を取り除く
  const distinctedRows = distinct(rows);

  // 重複があった場合に表示するメッセージを設定する
  const [message, setMessage] = useState(
    distinctedRows.length !== rows.length ? DISTINCT_MESSAGE : ""
  );

  // チェックボックスの状態を管理するstateを生成する
  const [checkBoxOptions, setCheckBoxOptions] = useState(
    makeOptionsInitialState(distinctedRows)
  );

  const [disableConfirmModalCancelButton, setDisableConfirmModalCancelButton] =
    useState<boolean>(false);

  const isOwnerInfoChecked = (
    checkBoxOptions: BookRequestCheckboxStatuses[]
  ): boolean => {
    for (const option of checkBoxOptions) {
      if (option.ownerInfo.checked) {
        return true;
      }
    }
    return false;
  };

  const isAllMattersChecked = (
    checkBoxOptions: BookRequestCheckboxStatuses[]
  ): boolean => {
    for (const option of checkBoxOptions) {
      if (
        option.kyoutan.checked ||
        option.sintaku.checked ||
        option.genzai.checked ||
        option.requestClosureBook.checked
      ) {
        return true;
      }
    }
    return false;
  };

  const { ownerInfoViaScraping, displayCheckboxAllMattersAnalyze } =
    useFeatureFlags();

  // Zodスキーマ定義
  const FormDataSchema = z.object({
    identifyName: z.string().max(256, "識別名は256文字以内です"),
    pastDays: z
      .number({ invalid_type_error: PAST_DAYS_TYPE_MESSAGE })
      .min(PAST_DAYS_MIN, PAST_DAYS_MIN_MESSAGE)
      .max(PAST_DAYS_MAX, PAST_DAYS_MAX_MESSAGE),
  });

  // 定義したZodのスキーマをTypescriptの型に変換
  type FormData = z.infer<typeof FormDataSchema>;

  // 型を用いてReact-Hook-Formのインスタンスを作る
  const reactHookForm = useForm<FormData>({
    mode: "all",
    defaultValues: {
      identifyName: "",
      pastDays: 0,
    },
    resolver: zodResolver(FormDataSchema),
  });

  useEffect(() => {
    // テキストボックスに何か入力してしばらく放置すると、再びサーバーからデータを取りに行った時に入力中の値が上書きされてしまう
    // そこで、一度でも入力があったら更新しないようにする
    if (reactHookForm.formState.isDirty) {
      return;
    }
    reactHookForm.setValue("pastDays", userConfig?.pictureRequestPastDays ?? 0);
  }, [userConfig]);

  useEffect(() => {
    // 該当のチェックボックスが全てチェックされていない場合、解析をfalseに変更する
    const ownerCheckList = checkBoxOptions.map(
      (option) => option.ownerInfo.checked
    );
    if (ownerCheckList.every((value) => !value)) {
      setWithOwnerAnalyze(false);
    }
    const allMattersCheckList = checkBoxOptions.map(
      (option) =>
        option.kyoutan.checked ||
        option.sintaku.checked ||
        option.genzai.checked ||
        option.requestClosureBook.checked
    );
    if (allMattersCheckList.every((value) => !value)) {
      setWithAllMattersAnalyze(false);
    }
  }, [checkBoxOptions]);

  useEffect(() => {
    // 種別が「閉鎖」の登記種別にチェックが入っていたら「共担」「信託」以外のチェックを非活性にする。
    // LANDとそれ以外でチェックボックスの状態は違うので場合分けして制御する
    const updatedCheckBoxOptions = checkBoxOptions.map((option, index) => {
      if (distinctedRows[index].bookType === "LAND") {
        if (option.requestClosureBook.checked) {
          return {
            ...option,
            genzai: { checked: false, disabled: true },
            ownerInfo: { checked: false, disabled: true },
            electricDrawings: { checked: false, disabled: true },
            tisekiDrawings: { checked: false, disabled: true },
            tiekiDrawings: { checked: false, disabled: true },
          };
        } else {
          return {
            ...option,
            genzai: { ...option.genzai, disabled: false },
            ownerInfo: { ...option.ownerInfo, disabled: false },
            electricDrawings: {
              ...option.electricDrawings,
              disabled: false,
            },
            tisekiDrawings: {
              ...option.tisekiDrawings,
              disabled: false,
            },
            tiekiDrawings: {
              ...option.tiekiDrawings,
              disabled: false,
            },
          };
        }
      } else {
        if (option.requestClosureBook.checked) {
          return {
            ...option,
            genzai: { checked: false, disabled: true },
            ownerInfo: { checked: false, disabled: true },
            buildingDrawings: { checked: false, disabled: true },
          };
        } else {
          return {
            ...option,
            genzai: { ...option.genzai, disabled: false },
            ownerInfo: { ...option.ownerInfo, disabled: false },
            buildingDrawings: { ...option.buildingDrawings, disabled: false },
          };
        }
      }
    });
    setCheckBoxOptions(updatedCheckBoxOptions);
  }, [checkBoxOptions]);

  return (
    <PagePaper>
      {message !== "" ? (
        <AlertBar
          message={message}
          onClose={() => {
            setMessage("");
          }}
        />
      ) : (
        ""
      )}
      <form>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6} md mb={1}>
            <Grid container spacing={1} justifyContent={"center"}>
              <Grid item xs={12} sm={6}>
                <Label>識別名</Label>
                <CTextFieldIdentifyName
                  name={"identifyName"}
                  control={reactHookForm.control}
                  size={SizingWrapperStyle.INHERIT}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Label required={true}>経過日数</Label>
                <CTextFieldPastDays
                  name="pastDays"
                  control={reactHookForm.control}
                  size={SizingWrapperStyle.INHERIT}
                  disabled={reuseOnlyOwn}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <Typography fontWeight={"bold"} align={"right"} fontSize={"0.9rem"}>
              列名をダブルクリックすると全選択する
            </Typography>
          </Grid>
        </Grid>
        <FormControlLabel
          control={
            <MuiCheckbox
              checked={reuseOnlyOwn}
              onChange={(e) => {
                setReuseOnlyOwn(e.target.checked);
              }}
            />
          }
          label="取得済不動産登記のみ取得する（初めて取得する地番・家屋番号が含まれる場合はこちらのチェックは外してください）"
        />
        <SectionPaper>
          <Box sx={{ height: isSmallHeightScreen ? 400 : 600, width: "100%" }}>
            <FeedGrid
              rows={distinctedRows}
              checkBoxOptions={checkBoxOptions}
              setCheckBoxOptions={setCheckBoxOptions}
              autoHeight={false}
              isOpenedTitleSearch={isOpenedTitleSearch}
            />
          </Box>
          {ownerInfoViaScraping ? (
            <Box sx={{ ...centerFlexBoxSx, mx: 2, mt: 6 }}>
              <FormControlLabel
                control={
                  <MuiCheckbox
                    sx={{ padding: "0px 4px" }}
                    checked={withOwnerAnalyze}
                    disabled={!isOwnerInfoChecked(checkBoxOptions)}
                    onChange={(e) => {
                      setWithOwnerAnalyze(e.target.checked);
                    }}
                  />
                }
                label="所有者事項の取得と同時に内容を解析する"
              />
            </Box>
          ) : null}
          {displayCheckboxAllMattersAnalyze ? (
            <>
              <Box sx={{ ...centerFlexBoxSx, mr: 2, my: 1 }}>
                <FormControlLabel
                  control={
                    <MuiCheckbox
                      sx={{ padding: "0px 4px" }}
                      checked={withAllMattersAnalyze}
                      disabled={!isAllMattersChecked(checkBoxOptions)}
                      onChange={(e) => {
                        setWithAllMattersAnalyze(e.target.checked);
                      }}
                    />
                  }
                  label="全部事項の取得と同時に内容を解析する"
                />
              </Box>
              {withAllMattersAnalyze && (
                <Box
                  sx={{
                    ...centerFlexBoxSx,
                    mr: 2,
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <AllMattersAnalyzeOptionsCheckbox
                    disabled={!isAllMattersChecked(checkBoxOptions)}
                    value={allMattersAnalyzeOptions}
                    onChange={(value) => {
                      setAllMattersAnalyzeOptions(value);
                      setAllMattersOptionsError(null);
                    }}
                  />
                  {allMattersOptionsError != null && (
                    <FormHelperText error sx={{ textAlign: "center" }}>
                      {allMattersOptionsError}
                    </FormHelperText>
                  )}
                </Box>
              )}
            </>
          ) : null}
          {/* 共通 Button は SP で幅100%になるため、内容幅で包む */}
          <Box sx={{ ...centerFlexBoxSx, mt: 1, mb: 2 }}>
            <Box sx={{ width: "fit-content" }}>
              <Button
                variant={ButtonVariantOption.Contained}
                onClick={() => {
                  void (async () => {
                    // 現在がチェックかつ信託か共担のどちらかがチェックされていなかった場合、メッセージを表示する
                    const isInvalidOption = checkBoxOptions.some((value) => {
                      if (
                        value.genzai.checked &&
                        !(value.sintaku.checked || value.kyoutan.checked)
                      ) {
                        alert(
                          "現在事項を取得する場合には共担か信託を選択してください"
                        );
                        return true;
                      }
                      return false;
                    });
                    if (isInvalidOption) return;
                    // 一つもチェックがない場合、メッセージを表示する
                    const result = checkBoxOptions.some((value) => {
                      return (
                        Object.keys(value) as Array<
                          keyof BookRequestCheckboxStatuses
                        >
                      ).some((key) => value[key].checked);
                    });
                    if (!result) {
                      alert(
                        "取得したい登記種別を最低一つはチェックしてください"
                      );
                      return;
                    }
                    // 全部事項解析ONかつ解析オプションが1つも選択されていない場合はエラー表示
                    if (
                      withAllMattersAnalyze &&
                      !allMattersAnalyzeOptions.includeTitleSection &&
                      !allMattersAnalyzeOptions.includeKoku &&
                      !allMattersAnalyzeOptions.includeOtsuku
                    ) {
                      setAllMattersOptionsError(
                        "解析オプションは最低1つ選択してください"
                      );
                      return;
                    }
                    setAllMattersOptionsError(null);
                    // 一つでもチェックがあれば通す。チェックされていない行は取得登録されない
                    await reactHookForm.handleSubmit((_data, _event) => {
                      setOpenModalFlag(true);
                    })();
                  })();
                }}
                label={"一括取得する"}
              />
            </Box>
          </Box>
        </SectionPaper>

        {/* 確認のモーダル画面 */}
        <ConfirmAcquireMultipleBooksDialog
          open={openModalFlag}
          setOpen={setOpenModalFlag}
          checkBoxOptions={checkBoxOptions}
          handleAcquireButtonClick={(_event, cancelLoading) => {
            handleSubmit(
              distinctedRows,
              checkBoxOptions,
              reactHookForm.getValues("pastDays"),
              withOwnerAnalyze,
              withAllMattersAnalyze,
              reuseOnlyOwn,
              reactHookForm.getValues("identifyName"),
              cancelLoading,
              setDisableConfirmModalCancelButton,
              allMattersAnalyzeOptions
            );
          }}
          disableConfirmModalCancelButton={disableConfirmModalCancelButton}
          isReadOnly={isReadOnly}
        />
      </form>
    </PagePaper>
  );
};
